import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { migrateArchivedProducts, checkMigrationStatus, validateMigration } from '../utils/productMigration';
import { COLORS } from '../lib/styleGuide';

// Style constants
const FONTS = {
  heading: { fontFamily: 'Inter, system-ui, sans-serif' },
  body: { fontFamily: 'Inter, system-ui, sans-serif' }
};

export default function AdminMigration() {
  const navigate = useNavigate();
  const { currentUser, hasRole } = useAuth();
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState(null);

  // Check if user is admin
  useEffect(() => {
    if (currentUser && !hasRole('admin')) {
      navigate('/');
      return;
    }
    
    if (currentUser && hasRole('admin')) {
      checkStatus();
    }
  }, [currentUser, hasRole, navigate]);

  const checkStatus = async () => {
    try {
      setError(null);
      const status = await checkMigrationStatus();
      setMigrationStatus(status);
    } catch (err) {
      console.error('Error checking migration status:', err);
      
      // Show more specific error messages
      let errorMessage = 'Failed to check migration status';
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. You may not have the right to access the database collections.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    }
  };

  const runMigration = async () => {
    if (!window.confirm('Are you sure you want to run the cleanup? This will remove archived flags from all archived products.')) {
      return;
    }

    try {
      setIsRunning(true);
      setError(null);
      
      console.log('Starting migration...');
      const result = await migrateArchivedProducts();
      
      if (result.success) {
        alert(`Cleanup completed successfully!\nCleaned: ${result.migrated} products\nErrors: ${result.errors.length}`);
      } else {
        alert(`Cleanup completed with errors!\nCleaned: ${result.migrated} products\nErrors: ${result.errors.length}`);
      }
      
      // Refresh status
      await checkStatus();
      
      // Run validation
      const validation = await validateMigration();
      setValidationResult(validation);
      
    } catch (err) {
      console.error('Error running migration:', err);
      
      // Show more specific error messages
      let errorMessage = 'Failed to run migration';
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. You may not have the right to create or modify database collections.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsRunning(false);
    }
  };

  const runValidation = async () => {
    try {
      setError(null);
      const validation = await validateMigration();
      setValidationResult(validation);
    } catch (err) {
      console.error('Error running validation:', err);
      setError(err.message || 'Failed to run validation');
    }
  };

  if (!currentUser || !hasRole('admin')) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#F9F9F9', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        paddingBottom: '120px',
        fontFamily: 'Montserrat, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 32, color: '#222', marginBottom: 16 }}>Access Denied</h1>
          <p style={{ color: '#757575', fontSize: 18 }}>You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F9F9F9',
      paddingBottom: '120px',
      fontFamily: 'Montserrat, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        background: COLORS.NEUTRAL_WHITE,
        borderBottom: '1px solid #E0E0E0',
        padding: '20px 16px'
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h1 style={{
              fontSize: 24,
              fontWeight: 700,
              color: COLORS.NEUTRAL_TEXT,
              margin: '0 0 4px 0',
              ...FONTS.heading
            }}>
              Data Migration
            </h1>
            <p style={{
              fontSize: 14,
              color: COLORS.ACCENT_GRAY,
              margin: 0,
              ...FONTS.body
            }}>
              Clean up archived products by removing archived flags
            </p>
          </div>
          
          <button
            onClick={() => navigate('/admin-dashboard')}
            style={{
              background: COLORS.PRIMARY_BROWN,
              color: COLORS.NEUTRAL_WHITE,
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              ...FONTS.body
            }}
          >
            Back to Admin
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '24px 16px'
      }}>
        {/* Migration Status */}
        <div style={{
          background: COLORS.NEUTRAL_WHITE,
          borderRadius: 12,
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            fontSize: 20,
            fontWeight: 600,
            color: COLORS.NEUTRAL_TEXT,
            margin: '0 0 16px 0',
            ...FONTS.heading
          }}>
            Migration Status
          </h2>
          
          {migrationStatus ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                background: migrationStatus.needsMigration ? '#FFF3CD' : '#D1ECF1',
                border: `1px solid ${migrationStatus.needsMigration ? '#FFEAA7' : '#BEE5EB'}`,
                borderRadius: 8,
                padding: '16px'
              }}>
                <div style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: COLORS.ACCENT_GRAY,
                  marginBottom: '4px',
                  ...FONTS.body
                }}>
                  Original Collection (Archived)
                </div>
                <div style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: migrationStatus.needsMigration ? '#856404' : '#0C5460',
                  ...FONTS.heading
                }}>
                  {migrationStatus.originalArchived}
                </div>
              </div>
              
              <div style={{
                background: '#D4EDDA',
                border: '1px solid #C3E6CB',
                borderRadius: 8,
                padding: '16px'
              }}>
                <div style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: COLORS.ACCENT_GRAY,
                  marginBottom: '4px',
                  ...FONTS.body
                }}>
                  New Archive Collection
                </div>
                <div style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: '#155724',
                  ...FONTS.heading
                }}>
                  {migrationStatus.archivedCollection}
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              color: COLORS.ACCENT_GRAY,
              ...FONTS.body
            }}>
              Loading migration status...
            </div>
          )}
          
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={checkStatus}
              disabled={isRunning}
              style={{
                background: COLORS.PRIMARY_BROWN,
                color: COLORS.NEUTRAL_WHITE,
                border: 'none',
                borderRadius: 8,
                padding: '12px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: isRunning ? 'not-allowed' : 'pointer',
                opacity: isRunning ? 0.6 : 1,
                ...FONTS.body
              }}
            >
              Refresh Status
            </button>
            
            {migrationStatus?.needsMigration && (
              <button
                onClick={runMigration}
                disabled={isRunning}
                style={{
                  background: '#DC3545',
                  color: COLORS.NEUTRAL_WHITE,
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: isRunning ? 'not-allowed' : 'pointer',
                  opacity: isRunning ? 0.6 : 1,
                  ...FONTS.body
                }}
              >
                                 {isRunning ? 'Running Cleanup...' : 'Run Cleanup'}
              </button>
            )}
            
            <button
              onClick={runValidation}
              disabled={isRunning}
              style={{
                background: '#28A745',
                color: COLORS.NEUTRAL_WHITE,
                border: 'none',
                borderRadius: 8,
                padding: '12px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: isRunning ? 'not-allowed' : 'pointer',
                opacity: isRunning ? 0.6 : 1,
                ...FONTS.body
              }}
            >
              Validate Migration
            </button>
          </div>
        </div>

        {/* Validation Results */}
        {validationResult && (
          <div style={{
            background: COLORS.NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: `2px solid ${validationResult.valid ? '#28A745' : '#DC3545'}`
          }}>
            <h2 style={{
              fontSize: 20,
              fontWeight: 600,
              color: COLORS.NEUTRAL_TEXT,
              margin: '0 0 16px 0',
              ...FONTS.heading
            }}>
              Validation Results
            </h2>
            
            <div style={{
              padding: '16px',
              background: validationResult.valid ? '#D4EDDA' : '#F8D7DA',
              border: `1px solid ${validationResult.valid ? '#C3E6CB' : '#F5C6CB'}`,
              borderRadius: 8,
              color: validationResult.valid ? '#155724' : '#721C24',
              ...FONTS.body
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                {validationResult.valid ? '✅ Validation Successful' : '❌ Validation Failed'}
              </div>
              <div>{validationResult.message}</div>
              
              {validationResult.status && (
                <div style={{ marginTop: '12px', fontSize: '14px' }}>
                  <div>Original archived: {validationResult.status.originalArchived}</div>
                  <div>Archived collection: {validationResult.status.archivedCollection}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div style={{
            background: COLORS.NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '2px solid #DC3545'
          }}>
            <h2 style={{
              fontSize: 20,
              fontWeight: 600,
              color: COLORS.NEUTRAL_TEXT,
              margin: '0 0 16px 0',
              ...FONTS.heading
            }}>
              Error
            </h2>
            
            <div style={{
              padding: '16px',
              background: '#F8D7DA',
              border: '1px solid #F5C6CB',
              borderRadius: 8,
              color: '#721C24',
              ...FONTS.body
            }}>
              {error}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div style={{
          background: COLORS.NEUTRAL_WHITE,
          borderRadius: 12,
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
                        <h2 style={{
                fontSize: 20,
                fontWeight: 600,
                color: COLORS.NEUTRAL_TEXT,
                margin: '0 0 16px 0',
                ...FONTS.heading
              }}>
                Cleanup Instructions
              </h2>
          
          <div style={{
            color: COLORS.NEUTRAL_TEXT,
            lineHeight: 1.6,
            ...FONTS.body
          }}>
            <p style={{ marginBottom: '12px' }}>
              This cleanup tool removes the <code>archived: true</code> flag from products in the 
              <code>products</code> collection, effectively restoring them to active status.
            </p>
            
            <p style={{ marginBottom: '12px' }}>
              <strong>What happens during cleanup:</strong>
            </p>
            
            <ul style={{ marginLeft: '20px', marginBottom: '12px' }}>
              <li>Products marked as archived have their archived flag removed</li>
              <li>Archived metadata fields are cleared</li>
              <li>Products become active again</li>
              <li>All product data is preserved</li>
            </ul>
            
            <p style={{ marginBottom: '12px' }}>
              <strong>Benefits:</strong>
            </p>
            
            <ul style={{ marginLeft: '20px' }}>
              <li>Cleaner active products collection</li>
              <li>Faster customer-facing queries</li>
              <li>Better data organization</li>
              <li>Improved performance</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
